// frontend/app/components/lessonPlans/teachsense/teachsenseText.js
//
// Text shaping shared by the Insights panel and every TeachSense section, so a
// count, a timestamp or an interpolated sentence reads the same wherever it is
// drawn. `fill` and `countLabel` moved down here from LessonInsightsPanel.js
// unchanged — they were private to it until TeachSense needed the same two.

/**
 * Interpolates `{placeholders}` in an already-translated sentence, so a
 * translator receives one whole clause instead of a pile of fragments that only
 * reassemble correctly in English word order.
 */
export const fill = (template, values) =>
  String(template).replace(/\{(\w+)\}/g, (token, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : token
  );

export const countLabel = (n, singular, plural, t) =>
  `${n} ${(t || ((value) => value))(n === 1 ? singular : plural)}`;

/**
 * A prompt key authored as `still_confusing` read back as "Still confusing".
 * Reflection answers arrive keyed by the author's own strings and carry no
 * labels, so the key is all there is to head the column with.
 */
export const humanizeKey = (key) => {
  const words = String(key || "")
    .replace(/[._-]+/g, " ")
    .trim();
  if (!words) return "";
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * "4m ago" for the live strip. Returns "" for anything unparseable rather than
 * "Invalid Date" — a missing timestamp should read as nothing, not as an error.
 *
 * Timestamps without an offset are parsed as local time by the platform, so a
 * naive UTC `last_activity_at` reads skewed by the viewer's offset. That is the
 * server's contract to fix, not something to guess at here.
 */
export const relativeTime = (value, t) => {
  const translate = t || ((v) => v);
  const then = Date.parse(String(value || ""));
  if (!Number.isFinite(then)) return "";
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 45) return translate("just now");
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return fill(translate("{n}m ago"), { n: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return fill(translate("{n}h ago"), { n: hours });
  return fill(translate("{n}d ago"), { n: Math.round(hours / 24) });
};

/** Active time on a lesson, in the coarsest unit that still says something. */
export const formatDuration = (seconds) => {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return "0s";
  if (total < 60) return `${Math.round(total)}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
};

/**
 * `ai_feedback` is a string on some rows and an object on others depending on
 * which evaluator produced it, so read it defensively and render nothing rather
 * than "[object Object]" when it is a shape this does not know.
 */
export const feedbackText = (value) => {
  if (typeof value === "string") return value.trim();
  if (value && typeof value === "object") {
    const text = value.feedback ?? value.text ?? value.message ?? value.summary;
    return typeof text === "string" ? text.trim() : "";
  }
  return "";
};

/**
 * Where that feedback came from. The evaluator degrades to a deterministic
 * scorer when the model is unavailable (design decision L6), and feedback the
 * model did not write must not be labelled as though it did.
 */
export const feedbackSource = (value) => {
  const source = value && typeof value === "object" ? value.source : null;
  return typeof source === "string" ? source.toLowerCase() : "";
};
